import { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Separator } from "./components/ui/separator";
import { 
  Phone, 
  MapPin, 
  Clock, 
  Navigation, 
  Menu, 
  X, 
  Star,
  Trees,
  Waves,
  Users,
  ChevronRight,
  Globe
} from "lucide-react";

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

// Translations
const translations = {
  bm: {
    nav: {
      home: "Utama",
      about: "Tentang Kami",
      facilities: "Kemudahan",
      gallery: "Galeri",
      visitor: "Maklumat Pelawat",
      contact: "Hubungi"
    },
    hero: {
      headline: "Destinasi Rekreasi Keluarga di Keningau",
      subtitle: "Nikmati keindahan alam semula jadi, kolam renang yang nyaman, dan suasana tenang bersama keluarga tercinta di jantung Sabah.",
      directions: "Dapatkan Arah",
      call: "Hubungi Kami"
    },
    about: {
      title: "Tentang Kami",
      subtitle: "Kolam Rekreasi Keningau",
      description: "Terletak di kawasan yang indah di Keningau, Sabah, Kolam Rekreasi Keningau adalah destinasi sempurna untuk keluarga yang mencari ketenangan dan keseronokan. Dengan kemudahan kolam renang, kawasan riadah, dan pemandangan alam yang menakjubkan, kami menawarkan pengalaman rekreasi yang tidak dapat dilupakan.",
      feature1: "Persekitaran Alam Semula Jadi",
      feature1_desc: "Dikelilingi oleh kehijauan dan keindahan alam Sabah",
      feature2: "Mesra Keluarga",
      feature2_desc: "Sesuai untuk semua peringkat umur",
      feature3: "Aktiviti Air",
      feature3_desc: "Kolam renang dan aktiviti air yang menyeronokkan"
    },
    facilities: {
      title: "Kemudahan & Aktiviti",
      subtitle: "Nikmati pelbagai aktiviti menarik untuk seluruh keluarga",
      pool: "Kolam Renang",
      pool_desc: "Kolam renang yang bersih dan selamat untuk semua umur",
      picnic: "Kawasan Piknik",
      picnic_desc: "Ruang terbuka yang luas untuk berkelah bersama keluarga",
      kids: "Taman Permainan Kanak-kanak",
      kids_desc: "Kawasan permainan yang selamat dan menyeronokkan",
      nature: "Pemandangan Alam",
      nature_desc: "Nikmati keindahan flora dan fauna tempatan"
    },
    gallery: {
      title: "Galeri",
      subtitle: "Lihat keindahan dan kemudahan di Kolam Rekreasi Keningau"
    },
    visitor: {
      title: "Maklumat Pelawat",
      hours: "Waktu Operasi",
      daily: "Setiap Hari",
      address: "Alamat",
      guidelines: "Panduan Pelawat",
      guideline1: "Patuhi peraturan keselamatan di kawasan kolam",
      guideline2: "Jaga kebersihan persekitaran",
      guideline3: "Kanak-kanak mesti ditemani oleh orang dewasa",
      guideline4: "Dilarang membawa makanan dari luar (tertakluk kepada syarat)",
      rating: "Rating Google"
    },
    contact: {
      title: "Hubungi Kami",
      subtitle: "Kami sedia membantu anda",
      phone: "Telefon",
      callNow: "Hubungi Sekarang",
      location: "Lokasi",
      getDirections: "Dapatkan Arah"
    },
    footer: {
      description: "Destinasi rekreasi keluarga terbaik di Keningau, Sabah. Nikmati ketenangan alam dan kemudahan moden.",
      quickLinks: "Pautan Pantas",
      contactInfo: "Maklumat Hubungan",
      rights: "Hak Cipta Terpelihara."
    }
  },
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      facilities: "Facilities",
      gallery: "Gallery",
      visitor: "Visitor Info",
      contact: "Contact"
    },
    hero: {
      headline: "Family Recreation Destination in Keningau",
      subtitle: "Experience the beauty of nature, comfortable swimming pools, and peaceful atmosphere with your beloved family in the heart of Sabah.",
      directions: "Get Directions",
      call: "Call Us"
    },
    about: {
      title: "About Us",
      subtitle: "Kolam Rekreasi Keningau",
      description: "Located in the beautiful area of Keningau, Sabah, Kolam Rekreasi Keningau is the perfect destination for families seeking tranquility and fun. With swimming pool facilities, recreational areas, and stunning natural views, we offer an unforgettable recreational experience.",
      feature1: "Natural Environment",
      feature1_desc: "Surrounded by Sabah's greenery and natural beauty",
      feature2: "Family Friendly",
      feature2_desc: "Suitable for all ages",
      feature3: "Water Activities",
      feature3_desc: "Fun swimming pools and water activities"
    },
    facilities: {
      title: "Facilities & Activities",
      subtitle: "Enjoy various exciting activities for the whole family",
      pool: "Swimming Pool",
      pool_desc: "Clean and safe swimming pool for all ages",
      picnic: "Picnic Area",
      picnic_desc: "Spacious open area for family picnics",
      kids: "Children's Playground",
      kids_desc: "Safe and fun play area",
      nature: "Natural Scenery",
      nature_desc: "Enjoy the beauty of local flora and fauna"
    },
    gallery: {
      title: "Gallery",
      subtitle: "See the beauty and facilities at Kolam Rekreasi Keningau"
    },
    visitor: {
      title: "Visitor Information",
      hours: "Operating Hours",
      daily: "Daily",
      address: "Address",
      guidelines: "Visitor Guidelines",
      guideline1: "Follow safety rules in pool areas",
      guideline2: "Keep the environment clean",
      guideline3: "Children must be accompanied by adults",
      guideline4: "Outside food may be restricted (subject to conditions)",
      rating: "Google Rating"
    },
    contact: {
      title: "Contact Us",
      subtitle: "We're here to help you",
      phone: "Phone",
      callNow: "Call Now",
      location: "Location",
      getDirections: "Get Directions"
    },
    footer: {
      description: "The best family recreation destination in Keningau, Sabah. Enjoy nature's tranquility and modern facilities.",
      quickLinks: "Quick Links",
      contactInfo: "Contact Info",
      rights: "All Rights Reserved."
    }
  }
};

// Navbar Component
const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: t.nav.home },
    { href: "#about", label: t.nav.about },
    { href: "#facilities", label: t.nav.facilities },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#visitor", label: t.nav.visitor },
    { href: "#contact", label: t.nav.contact },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-lg" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isScrolled ? "bg-primary" : "bg-white/20 backdrop-blur-sm"
            }`}>
              <Trees className={`w-6 h-6 ${isScrolled ? "text-white" : "text-white"}`} />
            </div>
            <span className={`font-heading font-bold text-lg ${
              isScrolled ? "text-primary" : "text-white"
            }`}>
              Kolam Rekreasi
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className={`nav-link font-body text-sm font-medium transition-colors ${
                  isScrolled 
                    ? "text-foreground hover:text-primary" 
                    : "text-white/90 hover:text-white"
                }`}
                data-testid={`nav-${link.href.replace('#', '')}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "bm" ? "en" : "bm")}
              data-testid="language-toggle"
              className={`lang-toggle flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isScrolled 
                  ? "bg-primary/10 text-primary hover:bg-primary/20" 
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
              }`}
            >
              <Globe className="w-4 h-4" />
              {lang === "bm" ? "EN" : "BM"}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${isScrolled ? "text-foreground" : "text-white"}`} />
              ) : (
                <Menu className={`w-6 h-6 ${isScrolled ? "text-foreground" : "text-white"}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu md:hidden absolute top-full left-0 right-0 bg-white shadow-xl ${
          isMobileMenuOpen ? "open" : "closed"
        }`}
        data-testid="mobile-menu"
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className="block w-full text-left py-2 px-4 rounded-lg text-foreground hover:bg-primary/10 hover:text-primary font-medium transition-colors"
            >
              {link.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section id="home" className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1761124469507-424d33a0b4f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMG5hdHVyZSUyMHBhcmslMjBzd2ltbWluZyUyMHBvb2wlMjBmYW1pbHl8ZW58MHx8fHwxNzcwMTEwMjcwfDA&ixlib=rb-4.1.0&q=85"
          alt="Kolam Rekreasi Keningau"
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight animate-fade-in-up">
            {t.hero.headline}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/90 leading-relaxed animate-fade-in-up animation-delay-100">
            {t.hero.subtitle}
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
            <Button 
              size="lg"
              className="btn-primary bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-2xl font-semibold"
              onClick={() => window.open("https://www.google.com/maps/dir/?api=1&destination=89000+Keningau+Sabah+Malaysia", "_blank")}
              data-testid="hero-directions-btn"
            >
              <Navigation className="w-5 h-5 mr-2" />
              {t.hero.directions}
            </Button>
            <Button 
              size="lg"
              className="btn-secondary bg-secondary hover:bg-secondary/90 text-white px-8 py-6 text-lg rounded-2xl font-semibold"
              onClick={() => window.location.href = "tel:087-391533"}
              data-testid="hero-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              {t.hero.call}
            </Button>
          </div>
        </div>

        {/* Quick Info Card */}
        <div className="absolute bottom-8 left-4 right-4 sm:left-auto sm:right-8 sm:w-auto">
          <div className="glass-card rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-8" data-testid="quick-info-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">8 AM - 6 PM</p>
                <p className="text-sm font-semibold text-foreground">Setiap Hari</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Google Rating</p>
                <p className="text-sm font-semibold text-foreground">4.0 (131 ulasan)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Trees, title: t.about.feature1, desc: t.about.feature1_desc },
    { icon: Users, title: t.about.feature2, desc: t.about.feature2_desc },
    { icon: Waves, title: t.about.feature3, desc: t.about.feature3_desc },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-background" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-card">
              <img
                src="https://images.unsplash.com/photo-1654180563895-313d904459a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxzYWJhaCUyMG1hbGF5c2lhJTIwbmF0dXJlJTIwcml2ZXIlMjBmb3Jlc3R8ZW58MHx8fHwxNzcwMTEwMjc0fDA&ixlib=rb-4.1.0&q=85"
                alt="Nature path in Sabah"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-2xl shadow-lg hidden md:block">
              <p className="text-3xl font-bold font-heading">4.0</p>
              <p className="text-sm opacity-90">Google Rating</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-secondary font-semibold text-sm tracking-wide uppercase">
              {t.about.title}
            </span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
              {t.about.subtitle}
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              {t.about.description}
            </p>

            <div className="mt-10 space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex gap-4 items-start info-card p-4 rounded-2xl bg-card shadow-card">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Facilities Section
const FacilitiesSection = () => {
  const { t } = useLanguage();

  const facilities = [
    {
      title: t.facilities.pool,
      desc: t.facilities.pool_desc,
      image: "https://images.unsplash.com/photo-1567491634123-460945ea86dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHx0cm9waWNhbCUyMG5hdHVyZSUyMHBhcmslMjBzd2ltbWluZyUyMHBvb2wlMjBmYW1pbHl8ZW58MHx8fHwxNzcwMTEwMjcwfDA&ixlib=rb-4.1.0&q=85",
      span: "col-span-1 md:col-span-2 row-span-2"
    },
    {
      title: t.facilities.picnic,
      desc: t.facilities.picnic_desc,
      image: "https://images.unsplash.com/photo-1654180639088-65f1bc3312d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxzYWJhaCUyMG1hbGF5c2lhJTIwbmF0dXJlJTIwcml2ZXIlMjBmb3Jlc3R8ZW58MHx8fHwxNzcwMTEwMjc0fDA&ixlib=rb-4.1.0&q=85",
      span: "col-span-1"
    },
    {
      title: t.facilities.kids,
      desc: t.facilities.kids_desc,
      image: "https://images.unsplash.com/photo-1674581512066-fcf371e4f3d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHx0cm9waWNhbCUyMG5hdHVyZSUyMHBhcmslMjBzd2ltbWluZyUyMHBvb2wlMjBmYW1pbHl8ZW58MHx8fHwxNzcwMTEwMjcwfDA&ixlib=rb-4.1.0&q=85",
      span: "col-span-1"
    },
    {
      title: t.facilities.nature,
      desc: t.facilities.nature_desc,
      image: "https://images.unsplash.com/photo-1654180674774-45dbe0b3bdca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxzYWJhaCUyMG1hbGF5c2lhJTIwbmF0dXJlJTIwcml2ZXIlMjBmb3Jlc3R8ZW58MHx8fHwxNzcwMTEwMjc0fDA&ixlib=rb-4.1.0&q=85",
      span: "col-span-1 md:col-span-2"
    },
  ];

  return (
    <section id="facilities" className="py-20 md:py-32 bg-sand" data-testid="facilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-secondary font-semibold text-sm tracking-wide uppercase">
            {t.facilities.title}
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
            {t.facilities.subtitle}
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[200px] md:auto-rows-[220px]">
          {facilities.map((facility, index) => (
            <div
              key={index}
              className={`bento-card relative rounded-3xl overflow-hidden group cursor-pointer ${facility.span}`}
              data-testid={`facility-card-${index}`}
            >
              <img
                src={facility.image}
                alt={facility.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                  {facility.title}
                </h3>
                <p className="text-white/80 text-sm mt-2 line-clamp-2">
                  {facility.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const { t } = useLanguage();

  const galleryImages = [
    {
      src: "https://images.unsplash.com/photo-1761124469507-424d33a0b4f9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Kolam Renang"
    },
    {
      src: "https://images.unsplash.com/photo-1654180563895-313d904459a5?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Laluan Alam"
    },
    {
      src: "https://images.unsplash.com/photo-1654180639088-65f1bc3312d6?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Sungai Sabah"
    },
    {
      src: "https://images.unsplash.com/photo-1567491634123-460945ea86dd?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Kolam Hutan"
    },
    {
      src: "https://images.unsplash.com/photo-1654180674774-45dbe0b3bdca?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Pemandangan Gunung"
    },
    {
      src: "https://images.unsplash.com/photo-1654180635272-13fc4be2ecf3?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Tasik Sabah"
    },
    {
      src: "https://images.unsplash.com/photo-1674581512066-fcf371e4f3d8?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
      alt: "Taman Tropika"
    },
    {
      src: "https://images.pexels.com/photos/34640971/pexels-photo-34640971.jpeg?w=800",
      alt: "Aktiviti Air"
    },
  ];

  return (
    <section id="gallery" className="py-20 md:py-32 bg-background" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-secondary font-semibold text-sm tracking-wide uppercase">
            {t.gallery.title}
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
            {t.gallery.subtitle}
          </h2>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className={`relative rounded-2xl overflow-hidden group cursor-pointer ${
                index === 0 || index === 5 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className={`gallery-image w-full object-cover ${
                  index === 0 || index === 5 ? "h-64 md:h-full" : "h-48 md:h-56"
                }`}
              />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors duration-300 flex items-center justify-center">
                <span className="text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {image.alt}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Visitor Info Section
const VisitorInfoSection = () => {
  const { t } = useLanguage();

  const guidelines = [
    t.visitor.guideline1,
    t.visitor.guideline2,
    t.visitor.guideline3,
    t.visitor.guideline4,
  ];

  return (
    <section id="visitor" className="py-20 md:py-32 bg-sand" data-testid="visitor-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-secondary font-semibold text-sm tracking-wide uppercase">
            {t.visitor.title}
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
            {t.visitor.address}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <div className="map-container rounded-3xl overflow-hidden border-4 border-white h-[400px]" data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3985.4!2d116.17!3d5.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s89000%20Keningau%2C%20Sabah!5e0!3m2!1sen!2smy!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Kolam Rekreasi Keningau Location"
            />
          </div>

          {/* Info Cards */}
          <div className="space-y-6">
            {/* Hours Card */}
            <Card className="info-card border-0 shadow-card rounded-3xl overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground">{t.visitor.hours}</h3>
                    <p className="text-muted-foreground">{t.visitor.daily}</p>
                    <p className="text-2xl font-bold text-primary mt-1">8:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rating Card */}
            <Card className="info-card border-0 shadow-card rounded-3xl overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center">
                    <Star className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground">{t.visitor.rating}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl font-bold text-secondary">4.0</span>
                      <div className="flex">
                        {[1, 2, 3, 4].map((star) => (
                          <Star key={star} className="w-5 h-5 fill-secondary text-secondary" />
                        ))}
                        <Star className="w-5 h-5 text-gray-300" />
                      </div>
                      <span className="text-muted-foreground">(131)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guidelines Card */}
            <Card className="info-card border-0 shadow-card rounded-3xl overflow-hidden">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-lg text-foreground mb-4">{t.visitor.guidelines}</h3>
                <ul className="space-y-3">
                  {guidelines.map((guideline, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{guideline}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();

  return (
    <section id="contact" className="py-20 md:py-32 bg-primary" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-4 text-lg text-white/80">
            {t.contact.subtitle}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center">
            {/* Call Button */}
            <Button
              size="lg"
              className="btn-secondary bg-white hover:bg-white/90 text-primary px-10 py-7 text-lg rounded-2xl font-bold shadow-lg"
              onClick={() => window.location.href = "tel:087-391533"}
              data-testid="contact-call-btn"
            >
              <Phone className="w-6 h-6 mr-3" />
              {t.contact.callNow}
              <span className="ml-2 text-primary/70">087-391 533</span>
            </Button>

            {/* Directions Button */}
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent hover:bg-white/10 text-white border-2 border-white px-10 py-7 text-lg rounded-2xl font-bold"
              onClick={() => window.open("https://www.google.com/maps/dir/?api=1&destination=89000+Keningau+Sabah+Malaysia", "_blank")}
              data-testid="contact-directions-btn"
            >
              <Navigation className="w-6 h-6 mr-3" />
              {t.contact.getDirections}
            </Button>
          </div>

          {/* Address */}
          <div className="mt-12 flex items-center justify-center gap-3 text-white/80">
            <MapPin className="w-5 h-5" />
            <span>89000 Keningau, Sabah, Malaysia</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-forest-dark text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <Trees className="w-7 h-7 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">Kolam Rekreasi Keningau</span>
            </div>
            <p className="text-white/70 leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              {[
                { href: "#home", label: t.nav.home },
                { href: "#about", label: t.nav.about },
                { href: "#facilities", label: t.nav.facilities },
                { href: "#gallery", label: t.nav.gallery },
                { href: "#visitor", label: t.nav.visitor },
              ].map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t.footer.contactInfo}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/70">
                <Phone className="w-5 h-5" />
                <a href="tel:087-391533" className="hover:text-white transition-colors">
                  087-391 533
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 mt-0.5" />
                <span>89000 Keningau, Sabah, Malaysia</span>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Clock className="w-5 h-5" />
                <span>8:00 AM - 6:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-white/20" />

        <div className="text-center text-white/60 text-sm">
          © {currentYear} Kolam Rekreasi Keningau. {t.footer.rights}
        </div>
      </div>
    </footer>
  );
};

// Main Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <AboutSection />
      <FacilitiesSection />
      <GallerySection />
      <VisitorInfoSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Language Provider Component
const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("bm");
  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
